import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, Clock, AlertTriangle, XCircle, User, 
  ArrowRight, ThumbsUp, ThumbsDown, MessageSquare,
  Calendar, Users, Building, Crown, Shield, Zap,
  GitBranch, Target, FileText, Settings, AlertCircle,
  PlayCircle, PauseCircle, SkipForward, RotateCcw,
  Database, Server, Layers, Link, Activity, Cpu,
  HardDrive, UserPlus, UserMinus, Package, Monitor, Smartphone
} from 'lucide-react';
import Button from '../../../../components/common/Button';
import WysiwygEditor from '../../../../components/common/WysiwygEditor';
import { calculateSLAInfo, getSLAStatusColor } from '../../../../utils/slaUtils';
import { updateTicket, getHelpdeskDepartments, getHelpdeskRoles, getDivisionDepartments } from '../../../../models/helpdeskModel';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../../../../config';

/**
 * Helper function to get integration status from ticket
 * Checks both direct property and custom_fields JSON
 */
const getIntegrationStatus = (ticket, key) => {
  // First check if it's a direct property
  if (ticket?.[key]) {
    return ticket[key];
  }
  
  // Then check in custom_fields
  try {
    const customFields = typeof ticket?.custom_fields === 'string' 
      ? JSON.parse(ticket.custom_fields) 
      : ticket?.custom_fields || {};
    return customFields?.[key] || 'no';
  } catch (e) {
    console.error('Error parsing custom_fields:', e);
    return 'no';
  }
};

/**
 * Dynamic Ticket Type Identifier
 */
export const TicketTypeHeader = ({ ticket }) => {
  const getTicketTypeInfo = () => {
    const type = ticket?.ticket_type?.toLowerCase() || '';
    const category = ticket?.category?.toLowerCase() || '';
    const subject = ticket?.subject?.toLowerCase() || '';
    
    // Access Management Types
    if (type.includes('access') || category.includes('access') || 
        subject.includes('new user') || subject.includes('staff movement') ||
        subject.includes('access modification') || subject.includes('termination')) {
      return {
        type: 'Access Management',
        icon: Shield,
        color: 'purple',
        requiresApproval: true,
        workflow: 'approval'
      };
    }
    
    // Email-to-Ticket
    if (ticket?.source === 'email' || type.includes('email')) {
      return {
        type: 'Email-to-Ticket',
        icon: MessageSquare,
        color: 'blue',
        requiresApproval: false,
        workflow: 'standard'
      };
    }
    
    // Chat-to-Ticket
    if (ticket?.source === 'chat' || type.includes('chat')) {
      return {
        type: 'Chat-to-Ticket',
        icon: MessageSquare,
        color: 'green',
        requiresApproval: false,
        workflow: 'standard'
      };
    }
    
    // Support Request Types
    if (type.includes('enh') || category.includes('enhancement')) {
      return {
        type: 'Enhancement Request',
        icon: Zap,
        color: 'orange',
        requiresApproval: true,
        workflow: 'approval'
      };
    }
    
    if (type.includes('def') || category.includes('defect') || category.includes('bug')) {
      return {
        type: 'Defect Report',
        icon: AlertTriangle,
        color: 'red',
        requiresApproval: false,
        workflow: 'standard'
      };
    }
    
    // Default Support
    return {
      type: 'Support Request',
      icon: FileText,
      color: 'blue',
      requiresApproval: false,
      workflow: 'standard'
    };
  };

  const typeInfo = getTicketTypeInfo();
  const IconComponent = typeInfo.icon;

  return (
    <div className={`flex items-center space-x-3 p-4 bg-${typeInfo.color}-50 border border-${typeInfo.color}-200 rounded-lg mb-4`}>
      <IconComponent className={`w-6 h-6 text-${typeInfo.color}-600`} />
      <div>
        <h3 className={`text-lg font-semibold text-${typeInfo.color}-900`}>
          {typeInfo.type}
        </h3>
        <p className={`text-sm text-${typeInfo.color}-700`}>
          {typeInfo.requiresApproval ? 'Requires Approval Workflow' : 'Standard IT Support Process'}
        </p>
      </div>
      {typeInfo.requiresApproval && (
        <div className={`ml-auto px-3 py-1 bg-${typeInfo.color}-100 text-${typeInfo.color}-800 text-xs font-medium rounded-full`}>
          Approval Required
        </div>
      )}
    </div>
  );
};

/**
 * Workflow Status Timeline
 */
export const WorkflowTimeline = ({ ticket, workflow }) => {
  const [currentStage, setCurrentStage] = useState(0);

  const getWorkflowStages = () => {
    const type = ticket?.ticket_type?.toLowerCase() || '';
    const requiresApproval = ticket?.requires_approval || 
                           type.includes('access') || 
                           type.includes('enh');

    if (!requiresApproval) {
      return [
        { name: 'Submitted', icon: FileText, status: 'completed' },
        { name: 'IT Review', icon: User, status: ticket?.status === 'open' ? 'current' : 'completed' },
        { name: 'In Progress', icon: PlayCircle, status: ticket?.status === 'in_progress' ? 'current' : 'pending' },
        { name: 'Resolved', icon: CheckCircle, status: ticket?.status === 'closed' ? 'completed' : 'pending' }
      ];
    }

    // Approval Workflow
    return [
      { name: 'Submitted', icon: FileText, status: 'completed' },
      { name: 'Manager Approval', icon: User, status: 'current' },
      { name: 'HOD Approval', icon: Building, status: 'pending' },
      { name: 'IT HOD Review', icon: Shield, status: 'pending' },
      { name: 'Director Approval', icon: Crown, status: 'pending' },
      { name: 'Implementation', icon: PlayCircle, status: 'pending' },
      { name: 'Completed', icon: CheckCircle, status: 'pending' }
    ];
  };

  const stages = getWorkflowStages();

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'current': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-gray-400 bg-gray-100';
      default: return 'text-gray-400 bg-gray-100';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
        <GitBranch className="w-5 h-5 mr-2 text-blue-600" />
        Workflow Progress
      </h3>
      
      <div className="relative">
        {stages.map((stage, index) => {
          const IconComponent = stage.icon;
          const isLast = index === stages.length - 1;
          
          return (
            <div key={index} className="relative flex items-center">
              {/* Timeline line */}
              {!isLast && (
                <div className="absolute left-6 top-12 w-0.5 h-16 bg-gray-200"></div>
              )}
              
              {/* Stage indicator */}
              <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 ${
                stage.status === 'completed' ? 'border-green-500 bg-green-50' :
                stage.status === 'current' ? 'border-blue-500 bg-blue-50' :
                'border-gray-300 bg-gray-50'
              }`}>
                <IconComponent className={`w-6 h-6 ${
                  stage.status === 'completed' ? 'text-green-600' :
                  stage.status === 'current' ? 'text-blue-600' :
                  'text-gray-400'
                }`} />
              </div>
              
              {/* Stage content */}
              <div className="ml-4 pb-8">
                <h4 className={`font-medium ${
                  stage.status === 'completed' ? 'text-green-900' :
                  stage.status === 'current' ? 'text-blue-900' :
                  'text-gray-500'
                }`}>
                  {stage.name}
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  {stage.status === 'completed' ? 'Completed' :
                   stage.status === 'current' ? 'In Progress' :
                   'Pending'}
                </p>
                
                {/* Current stage details */}
                {stage.status === 'current' && (
                  <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      This stage is currently active. Next action required.
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Approval Panel for Managers/HODs/Directors
 */
export const ApprovalPanel = ({ ticket, userRole, onApprovalAction }) => {
  const [approvalNote, setApprovalNote] = useState('');
  const [showApprovalForm, setShowApprovalForm] = useState(false);
  const [selectedAction, setSelectedAction] = useState('');

  const canApprove = () => {
    const ticketStage = ticket?.workflow_stage || 'manager_approval';
    return (
      (userRole === 'manager' && ticketStage === 'manager_approval') ||
      (userRole === 'hod' && ticketStage === 'hod_approval') ||
      (userRole === 'it_hod' && ticketStage === 'it_hod_review') ||
      (userRole === 'director' && ticketStage === 'director_approval')
    );
  };

  const handleApprovalAction = async (action) => {
    setSelectedAction(action);
    setShowApprovalForm(true);
  };

  const submitApproval = async () => {
    if (!selectedAction) return;
    
    try {
      await onApprovalAction({
        action: selectedAction,
        notes: approvalNote,
        ticketId: ticket.id
      });
      
      setShowApprovalForm(false);
      setApprovalNote('');
      setSelectedAction('');
    } catch (error) {
      console.error('Error processing approval:', error);
    }
  };

  if (!canApprove()) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
        <div className="text-center">
          <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Waiting for Approval
          </h3>
          <p className="text-gray-600">
            This ticket is pending approval from another level.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
        <Target className="w-5 h-5 mr-2 text-blue-600" />
        Approval Requested
      </h3>

      {!showApprovalForm ? (
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
              <p className="text-yellow-800 font-medium">
                Your approval is requested for this {ticket?.ticket_type || 'request'}
              </p>
            </div>
            <p className="text-yellow-700 mt-2 text-sm">
              Please review the request details and provide your decision.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Button
              onClick={() => handleApprovalAction('approve')}
              variant="success"
              icon={ThumbsUp}
              className="w-full"
            >
              Approve
            </Button>
            
            <Button
              onClick={() => handleApprovalAction('approve_with_conditions')}
              variant="warning"
              icon={MessageSquare}
              className="w-full"
            >
              Approve with Conditions
            </Button>
            
            <Button
              onClick={() => handleApprovalAction('request_more_info')}
              variant="secondary"
              icon={MessageSquare}
              className="w-full"
            >
              Request More Info
            </Button>
            
            <Button
              onClick={() => handleApprovalAction('reject')}
              variant="danger"
              icon={ThumbsDown}
              className="w-full"
            >
              Reject
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-medium text-gray-900">
              {selectedAction === 'approve' ? 'Approve Request' :
               selectedAction === 'approve_with_conditions' ? 'Approve with Conditions' :
               selectedAction === 'request_more_info' ? 'Request More Information' :
               'Reject Request'}
            </h4>
            <Button
              onClick={() => setShowApprovalForm(false)}
              variant="secondary"
              className="text-sm"
            >
              Cancel
            </Button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {selectedAction === 'approve' ? 'Approval Notes (Optional)' :
               selectedAction === 'approve_with_conditions' ? 'Conditions and Notes' :
               selectedAction === 'request_more_info' ? 'Information Requested' :
               'Rejection Reason'}
            </label>
            <WysiwygEditor
              value={approvalNote}
              onChange={setApprovalNote}
              placeholder={
                selectedAction === 'approve' ? 'Add any notes about this approval...' :
                selectedAction === 'approve_with_conditions' ? 'Specify the conditions for approval...' :
                selectedAction === 'request_more_info' ? 'What additional information do you need?' :
                'Please explain why this request is being rejected...'
              }
            />
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              onClick={() => setShowApprovalForm(false)}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={submitApproval}
              variant={selectedAction === 'reject' ? 'danger' : 'primary'}
              disabled={selectedAction !== 'approve' && !approvalNote.trim()}
            >
              {selectedAction === 'approve' ? 'Approve Request' :
               selectedAction === 'approve_with_conditions' ? 'Approve with Conditions' :
               selectedAction === 'request_more_info' ? 'Request Information' :
               'Reject Request'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Ticket Type-Specific Form Fields
 */
export const DynamicTicketFields = ({ ticket, isEditing, onFieldChange }) => {
  const getTicketTypeFields = () => {
    const type = ticket?.ticket_type?.toLowerCase() || '';
    const category = ticket?.category?.toLowerCase() || '';
    
    // Access Management Fields
    if (type.includes('access') || category.includes('access')) {
      return [
        { name: 'access_type', label: 'Access Type', type: 'select', options: [
          'New User Setup', 'Staff Movement', 'Access Modification', 'Access Termination'
        ]},
        { name: 'department', label: 'Department', type: 'text' },
        { name: 'position', label: 'Position/Role', type: 'text' },
        { name: 'manager', label: 'Direct Manager', type: 'text' },
        { name: 'start_date', label: 'Start Date', type: 'date' },
        { name: 'access_level', label: 'Access Level Required', type: 'select', options: [
          'Basic User', 'Power User', 'Admin', 'Super Admin'
        ]},
        { name: 'systems_required', label: 'Systems/Applications', type: 'textarea' }
      ];
    }
    
    // Enhancement Request Fields
    if (type.includes('enh') || category.includes('enhancement')) {
      return [
        { name: 'business_justification', label: 'Business Justification', type: 'textarea' },
        { name: 'expected_outcome', label: 'Expected Outcome', type: 'textarea' },
        { name: 'affected_users', label: 'Number of Affected Users', type: 'number' },
        { name: 'implementation_timeline', label: 'Requested Timeline', type: 'select', options: [
          'Urgent (1-2 weeks)', 'High (1 month)', 'Medium (3 months)', 'Low (6+ months)'
        ]},
        { name: 'budget_impact', label: 'Budget Impact', type: 'select', options: [
          'No Cost', 'Low (<$1000)', 'Medium ($1000-$5000)', 'High (>$5000)', 'Unknown'
        ]}
      ];
    }
    
    // Defect Report Fields
    if (type.includes('def') || category.includes('defect')) {
      return [
        { name: 'severity', label: 'Severity', type: 'select', options: [
          'Critical - System Down', 'High - Major Function Impacted', 'Medium - Minor Issue', 'Low - Cosmetic'
        ]},
        { name: 'affected_system', label: 'Affected System', type: 'text' },
        { name: 'steps_to_reproduce', label: 'Steps to Reproduce', type: 'textarea' },
        { name: 'expected_behavior', label: 'Expected Behavior', type: 'textarea' },
        { name: 'actual_behavior', label: 'Actual Behavior', type: 'textarea' },
        { name: 'frequency', label: 'How Often Does This Occur?', type: 'select', options: [
          'Always', 'Often', 'Sometimes', 'Rarely', 'Once'
        ]}
      ];
    }

    // Default Support Fields
    return [
      { name: 'urgency', label: 'Urgency', type: 'select', options: [
        'Low - Can wait', 'Medium - Within this week', 'High - Within 24 hours', 'Critical - Immediate'
      ]},
      { name: 'affected_users', label: 'Number of Affected Users', type: 'number' },
      { name: 'business_impact', label: 'Business Impact', type: 'textarea' }
    ];
  };

  const fields = getTicketTypeFields();

  const renderField = (field) => {
    const value = ticket?.[field.name] || '';
    
    switch (field.type) {
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => onFieldChange(field.name, e.target.value)}
            disabled={!isEditing}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">Select {field.label}</option>
            {field.options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => onFieldChange(field.name, e.target.value)}
            disabled={!isEditing}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            placeholder={`Enter ${field.label.toLowerCase()}...`}
          />
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => onFieldChange(field.name, e.target.value)}
            disabled={!isEditing}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            placeholder={`Enter ${field.label.toLowerCase()}...`}
          />
        );
      
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => onFieldChange(field.name, e.target.value)}
            disabled={!isEditing}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        );
      
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => onFieldChange(field.name, e.target.value)}
            disabled={!isEditing}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            placeholder={`Enter ${field.label.toLowerCase()}...`}
          />
        );
    }
  };

};

/**
 * Delegation Management Interface
 */
export const DelegationPanel = ({ userRole, onSetDelegate }) => {
  const [showDelegateForm, setShowDelegateForm] = useState(false);
  const [delegateUser, setDelegateUser] = useState('');
  const [delegateReason, setDelegateReason] = useState('');
  const [delegateUntil, setDelegateUntil] = useState('');

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
        <Users className="w-5 h-5 mr-2 text-blue-600" />
        Delegation Management
      </h3>

      {!showDelegateForm ? (
        <div className="text-center">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">
            Delegate your approval authority when you're out of office
          </p>
          <Button
            onClick={() => setShowDelegateForm(true)}
            variant="secondary"
            icon={SkipForward}
          >
            Set Up Delegation
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Delegate To
            </label>
            <select
              value={delegateUser}
              onChange={(e) => setDelegateUser(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">Select user...</option>
              <option value="user1">John Smith (Manager)</option>
              <option value="user2">Jane Doe (HOD)</option>
              <option value="user3">Mike Johnson (Deputy)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Until Date
            </label>
            <input
              type="date"
              value={delegateUntil}
              onChange={(e) => setDelegateUntil(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason (Optional)
            </label>
            <textarea
              value={delegateReason}
              onChange={(e) => setDelegateReason(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Annual leave, business trip, etc."
            />
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              onClick={() => setShowDelegateForm(false)}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                onSetDelegate({
                  delegateUser,
                  delegateUntil,
                  delegateReason
                });
                setShowDelegateForm(false);
              }}
              disabled={!delegateUser || !delegateUntil}
            >
              Set Delegation
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * SLA Tracking Component
 */
export const SLATracker = ({ ticket }) => {
  const sla = calculateSLAInfo(ticket);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
        <Calendar className="w-5 h-5 mr-2 text-blue-600" />
        SLA Tracking
      </h3>

      <div className="space-y-6">
        {/* Response Time */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Response Time
            </span>
            <span className={`text-sm font-medium`} style={{ 
              color: getSLAStatusColor(sla.response_status) 
            }}>
              {sla.response_time}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="h-2 rounded-full transition-all duration-300"
              style={{ 
                width: `${Math.min(sla.responsePercentage, 100)}%`,
                backgroundColor: getSLAStatusColor(sla.response_status)
              }}
            ></div>
          </div>
          {sla.response_status === 'overdue' && (
            <p className="text-xs text-red-600 mt-1">⚠ Response SLA breached</p>
          )}
          {sla.response_status === 'due_soon' && (
            <p className="text-xs text-orange-600 mt-1">⚡ Response SLA due soon</p>
          )}
        </div>

        {/* Resolution Time */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Resolution Time
            </span>
            <span className={`text-sm font-medium`} style={{ 
              color: getSLAStatusColor(sla.resolution_status) 
            }}>
              {sla.resolution_time}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="h-2 rounded-full transition-all duration-300"
              style={{ 
                width: `${Math.min(sla.resolutionPercentage, 100)}%`,
                backgroundColor: getSLAStatusColor(sla.resolution_status)
              }}
            ></div>
          </div>
          {sla.resolution_status === 'overdue' && (
            <p className="text-xs text-red-600 mt-1">⚠ Resolution SLA breached</p>
          )}
          {sla.resolution_status === 'due_soon' && (
            <p className="text-xs text-orange-600 mt-1">⚡ Resolution SLA due soon</p>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Cross-Module Integration Status Panel
 */
export const IntegrationStatusPanel = ({ ticket, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  
  // Only show for specific workflow types that require cross-module integrations
  const isWorkflowTicket = ticket?.ticket_type && [
    'new_user', 'staff_movement', 'access_termination'
  ].includes(ticket.ticket_type);

  if (!isWorkflowTicket) {
    return null;
  }

  // Parse custom fields to check requirements
  let customFields = {};
  try {
    customFields = typeof ticket?.custom_fields === 'string' 
      ? JSON.parse(ticket.custom_fields) 
      : ticket?.custom_fields || {};
  } catch (e) {
    customFields = {};
  }

  // Define integration steps based on ticket type
  let integrationSteps = [];
  
  if (ticket?.ticket_type === 'access_termination') {
    integrationSteps = [
      {
        key: 'asset_deallocated',
        label: 'Asset Deallocation',
        icon: Monitor,
        description: 'Hardware deallocation and asset return',
        status: getIntegrationStatus(ticket, 'asset_deallocated'),
        module: 'Asset Management System'
      },
      {
        key: 'user_deactivated',
        label: 'User Account Deactivation',
        icon: UserMinus,
        description: 'Portal account deactivation and access removal',
        status: getIntegrationStatus(ticket, 'user_deactivated'),
        module: 'User Management System'
      }
    ];
  } else if (ticket?.ticket_type === 'staff_movement') {
    // Staff movement: Always show user update, conditionally show computer steps
    integrationSteps = [
      {
        key: 'user_created',
        label: 'Role & Department Update',
        icon: UserPlus,
        description: 'Update user role and department assignment',
        status: getIntegrationStatus(ticket, 'user_created'),
        module: 'User Management System'
      }
    ];
    
    // Only add computer-related steps if Computer was selected
    if (customFields.software_computer) {
      integrationSteps.push(
        {
          key: 'asset_created', 
          label: 'Asset Allocation',
          icon: Monitor,
          description: 'Hardware allocation and asset tracking',
          status: getIntegrationStatus(ticket, 'asset_created'),
          module: 'Asset Management System'
        },
        {
          key: 'asset_declared',
          label: 'User Declaration',
          icon: Package,
          description: 'User confirmation of resource receipt',
          status: getIntegrationStatus(ticket, 'asset_declared'),
          module: 'Declaration System'
        }
      );
    }
  } else {
    // Default for new_user and other workflow types
    integrationSteps = [
      {
        key: 'user_created',
        label: 'User Account Management',
        icon: UserPlus,
        description: 'Portal account creation and role assignment',
        status: getIntegrationStatus(ticket, 'user_created'),
        module: 'User Management System'
      }
    ];
    
    // Only add asset-related steps if Computer was selected
    if (customFields.software_computer) {
      integrationSteps.push(
        {
          key: 'asset_created', 
          label: 'Asset Allocation',
          icon: Monitor,
          description: 'Hardware allocation and asset tracking',
          status: getIntegrationStatus(ticket, 'asset_created'),
          module: 'Asset Management System'
        },
        {
          key: 'asset_declared',
          label: 'User Declaration',
          icon: Package,
          description: 'User confirmation of resource receipt',
          status: getIntegrationStatus(ticket, 'asset_declared'),
          module: 'Declaration System'
        }
      );
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'yes': case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'failed': case 'error': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-500 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'yes': case 'completed': return CheckCircle;
      case 'in_progress': return Clock;
      case 'failed': case 'error': return XCircle;
      case 'pending': return AlertTriangle;
      default: return Clock;
    }
  };

  const refreshIntegrationStatus = async () => {
    setLoading(true);
    try {
      await onRefresh?.();
      toast.success('Integration status refreshed');
    } catch (error) {
      toast.error('Failed to refresh status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Layers className="w-5 h-5 mr-2 text-blue-600" />
          Cross-Module Integration Status
        </h3>
        <Button
          onClick={refreshIntegrationStatus}
          variant="secondary"
          size="sm"
          icon={Activity}
          disabled={loading}
          className="text-sm"
        >
          {loading ? 'Refreshing...' : 'Refresh Status'}
        </Button>
      </div>

      <div className="space-y-4">
        {integrationSteps.map((step) => {
          const StatusIcon = getStatusIcon(step.status);
          const statusColor = getStatusColor(step.status);
          const StepIcon = step.icon;

          return (
            <div key={step.key} className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg">
              <div className="flex-shrink-0">
                <StepIcon className="w-6 h-6 text-blue-600" />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-sm font-medium text-gray-900">
                    {step.label}
                  </h4>
                  <div className={`flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                    <StatusIcon className="w-3 h-3 mr-1" />
                    <span className="capitalize">
                      {step.status === 'yes' ? 'Completed' : 
                       step.status === 'no' ? 'Pending' :
                       step.status}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-2">{step.description}</p>
                <p className="text-xs text-gray-500 flex items-center">
                  <Database className="w-3 h-3 mr-1" />
                  {step.module}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Integration Summary */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Server className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-900">Integration Progress</span>
          </div>
          <span className="text-sm text-gray-600">
            {integrationSteps.filter(s => s.status === 'yes').length} / {integrationSteps.length} completed
          </span>
        </div>
        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ 
              width: `${(integrationSteps.filter(s => s.status === 'yes').length / integrationSteps.length) * 100}%` 
            }}
          />
        </div>
      </div>

      {/* Allocated Assets Summary */}
      {(() => {
        try {
          const cf = ticket?.custom_fields ? (typeof ticket.custom_fields === 'string' ? JSON.parse(ticket.custom_fields) : ticket.custom_fields) : {};
          const allocated = cf?.integration_results?.asset_created?.allocated_asset_ids;
          const rows = Array.isArray(allocated) ? allocated : [];
          if (rows.length === 0) return null;
          return (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Allocated Assets</h4>
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-2">Asset ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((id) => (
                      <tr key={id} className="border-t">
                        <td className="p-2">{id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-[11px] text-gray-500">Tip: Open Assets {'>'} Hardware to view full details and declaration status.</p>
            </div>
          );
        } catch (e) {
          return null;
        }
      })()}
    </div>
  );
};

/**
 * Implementation Execution Panel - For IT Staff to Execute Integrations
 */
export const ImplementationPanel = ({ ticket, onIntegrationExecuted }) => {
  const [loading, setLoading] = useState({});
  const [executionNotes, setExecutionNotes] = useState({});
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [selectedAssetIds, setSelectedAssetIds] = useState([]);
  const [allocating, setAllocating] = useState(false);
  const [assetsFallbackNote, setAssetsFallbackNote] = useState('');
  const [allowReassign, setAllowReassign] = useState(false);
  const [assetSearch, setAssetSearch] = useState('');
  const [integrationCache, setIntegrationCache] = useState({}); // Cache integration results

  // Only show for workflow types that require cross-module integrations
  const requiresIntegrations = ticket?.ticket_type && [
    'new_user', 'staff_movement', 'access_termination'
  ].includes(ticket.ticket_type);

  if (!requiresIntegrations) {
    return null;
  }

  // Check if this ticket is ready for implementation
  const isReadyForImplementation = () => {
    // Check approval status based on required approvers
    if (ticket?.ticket_approvers) {
      const approvers = typeof ticket.ticket_approvers === 'string' 
        ? JSON.parse(ticket.ticket_approvers) 
        : ticket.ticket_approvers;
      
      // All required approvers must have approved (status = 1)
      const allApproved = approvers.every(approverType => {
        switch(approverType) {
          case 'manager': return ticket?.ticket_manager === 1;
          case 'hod': return ticket?.ticket_hod === 1;
          case 'it_hod': return ticket?.ticket_it_hod === 1;
          case 'director': return ticket?.ticket_director === 1;
          default: 
            // Handle specific HOD approvers (e.g., 'hod_93')
            if (approverType.startsWith('hod_')) {
              return ticket?.ticket_hod === 1;
            }
            return false;
        }
      });
      
      console.log('🔍 Approval check:', {
        approvers,
        ticket_manager: ticket?.ticket_manager,
        ticket_hod: ticket?.ticket_hod,
        ticket_it_hod: ticket?.ticket_it_hod,
        ticket_director: ticket?.ticket_director,
        allApproved
      });
      
      return allApproved;
    }
    
    // Fallback: check assessment and recommendation for older tickets
    return ticket?.ticket_assessment === 1 && ticket?.ticket_recommended === 1;
  };

  const executeIntegration = async (integrationType) => {
    // Validate ticket data first - check for various possible ID fields
    const ticketId = ticket?.id || ticket?.ticket_id || ticket?.ID || ticket?.Ticket_ID;
    if (!ticket || !ticketId) { 
      toast.error('Invalid ticket data. Please refresh and try again.');
      return;
    }

    // Load reference data for ID-to-name mapping
    let referenceData = { departments: [], divisionDepartments: [], roles: [] };
    try {
      const [departmentsRes, divisionDepartmentsRes, rolesRes] = await Promise.all([
        getHelpdeskDepartments().catch(() => ({ data: [] })),
        getDivisionDepartments().catch(() => ({ data: [] })),
        getHelpdeskRoles().catch(() => ({ data: [] }))
      ]);
      
      const deptList = departmentsRes?.departments || departmentsRes?.data?.departments || departmentsRes?.data || [];
      const divDeptList = divisionDepartmentsRes?.division_departments || divisionDepartmentsRes?.data?.division_departments || divisionDepartmentsRes?.data || [];
      const roleList = rolesRes?.roles || rolesRes?.data?.roles || rolesRes?.data || [];
      
      referenceData = {
        departments: deptList,
        divisionDepartments: divDeptList,
        roles: roleList
      };
    } catch (error) {
      console.warn('Failed to load reference data:', error);
    }

    // Extract user information from various possible sources
    const getUserInfo = () => {
      // Try to get from custom fields first
      let customFields = {};
      if (ticket?.custom_fields) {
        try {
          customFields = typeof ticket.custom_fields === 'string' 
            ? JSON.parse(ticket.custom_fields) 
            : ticket.custom_fields;
        } catch (e) {
          console.warn('Failed to parse custom fields:', e);
          customFields = {};
        }
      }
      
      // Helper to coerce mixed values like "Department ID: 27" -> 27
      const asNumber = (val) => {
        if (val === null || val === undefined) return undefined;
        if (typeof val === 'number') return val;
        const m = String(val).match(/(\d+)/);
        return m ? Number(m[1]) : undefined;
      };

      // Extract user information with multiple fallback options
      const userName = customFields.user_full_name || 
                      customFields.full_name || 
                      customFields.employee_name ||
                      customFields.name ||
                      // For new user tickets, combine first_name and last_name
                      (customFields.first_name && customFields.last_name ? 
                        `${customFields.first_name} ${customFields.last_name}` : null) ||
                      customFields.first_name ||
                      customFields.last_name ||
                      // Extract from ticket title/subject
                      ticket?.title?.match(/:\s*(.+)$/)?.[1]?.trim() ||
                      ticket?.subject?.match(/:\s*(.+)$/)?.[1]?.trim() ||
                      ticket?.subject?.match(/(?:for|user|employee)\s+([A-Za-z\s]+)/i)?.[1]?.trim() ||
                      ticket?.requested_by_name ||
                      ticket?.created_by_name ||
                      ticket?.requester_name ||
                      (ticket?.subject && ticket.subject.length > 0 ? 
                        `User from: ${ticket.subject.substring(0, 30)}...` : 
                        `User from ticket ${ticket?.ticket_number || ticketId || 'N/A'}`);

      // For staff_movement, show NEW department (not current)
      // Use EXACT same logic as TicketDetailsModal Additional Information card
      let department = 'Unspecified';
      let deptIdRaw;
      
      if (ticket?.ticket_type === 'staff_movement') {
        // Staff movement stores new department in "new_department" field
        deptIdRaw = customFields.new_department || customFields.department;
      } else {
        deptIdRaw = customFields.department || customFields.dept || customFields.user_department || ticket?.department;
      }
      
      if (deptIdRaw) {
        // Try divisionDepartments first (8 parent departments) - matches Additional Info logic
        let found = referenceData?.divisionDepartments?.find(d => 
          d.department_id == deptIdRaw || d.id == deptIdRaw || d.value == deptIdRaw
        );
        
        // Fallback to departments (37 divisions)
        if (!found && referenceData?.departments?.length > 0) {
          found = referenceData.departments.find(d => 
            d.department_id == deptIdRaw || d.id == deptIdRaw || d.value == deptIdRaw
          );
        }
        
        if (found) {
          department = found.department_name || found.name || found.label;
        }
      }

      // Get role/position - use EXACT same logic as TicketDetailsModal Additional Information card
      let role = 'Unspecified';
      let roleIdRaw;
      
      if (ticket?.ticket_type === 'staff_movement') {
        // Staff movement stores new position in "new_position" field
        roleIdRaw = customFields.new_position || customFields.assigned_post;
      } else if (ticket?.ticket_type === 'access_termination') {
        roleIdRaw = customFields.current_post || customFields.position || customFields.role;
      } else {
        roleIdRaw = customFields.post || customFields.position || customFields.role;
      }
      
      if (roleIdRaw && referenceData?.roles?.length > 0) {
        const roleObj = referenceData.roles.find(r => 
          r.role_id == roleIdRaw || r.id == roleIdRaw || r.value == roleIdRaw
        );
        if (roleObj) {
          role = roleObj.role_name || roleObj.name || roleObj.label;
        } else {
          // If it's a string that's not an ID, use it as-is (for current_post text)
          if (typeof roleIdRaw === 'string' && isNaN(roleIdRaw)) {
            role = roleIdRaw;
          }
        }
      }

      return { userName, department, role, customFields };
    };

  const { userName, department, role, customFields } = getUserInfo();

    // Generate additional details for better confirmation
    const additionalDetails = [];
    if (customFields.employee) additionalDetails.push(`Employee: ${customFields.employee}`);
    if (customFields.division) additionalDetails.push(`Division: ${customFields.division}`);
    if (customFields.required_by) additionalDetails.push(`Required By: ${customFields.required_by}`);
    if (customFields.date_birth) additionalDetails.push(`DOB: ${customFields.date_birth}`);
    
    // Generate software requirements list
    const softwareList = [];
    if (customFields.software_computer) softwareList.push('Computer Access');
    if (customFields.software_edams_billing) softwareList.push('EDAMS Billing');
    if (customFields.software_edams_operations) softwareList.push('EDAMS Operations');
    if (customFields.software_asset_management) softwareList.push('Asset Management');
    if (customFields.software_office) softwareList.push('Office Suite');
    if (customFields.software_imqs) softwareList.push('IMQS');
    if (customFields.software_other) softwareList.push(`Other: ${customFields.software_other}`);

    // Generate modal content based on integration type
    let modalContent;
    
    if (integrationType === 'asset_deallocated') {
      // Asset deallocation modal
      const terminationReason = customFields.termination_reason || customFields.reason || 'Not specified';
      const terminationComment = customFields.comment || customFields.termination_comment || '';
      
      modalContent = `
        <div style="text-align: left;">
          <p><strong>User Being Terminated:</strong></p>
          <div style="margin: 10px 0; padding: 15px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px;">
            <div style="font-size: 16px; font-weight: bold; color: #991b1b; margin-bottom: 8px;">${userName}</div>
          </div>
          
          ${terminationReason !== 'Not specified' ? `
          <p style="margin-top: 15px;"><strong>Termination Reason:</strong></p>
          <div style="margin: 10px 0; padding: 10px; background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 6px;">
            <div style="font-size: 13px; color: #374151;">${terminationReason}</div>
          </div>
          ` : ''}
          
          ${terminationComment ? `
          <p style="margin-top: 15px;"><strong>Additional Notes:</strong></p>
          <div style="margin: 10px 0; padding: 10px; background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 6px;">
            <div style="font-size: 13px; color: #374151;">${terminationComment}</div>
          </div>
          ` : ''}
          
          <p style="margin-top: 15px;"><strong>Action:</strong></p>
          <div style="margin: 10px 0; padding: 10px; background: #fff7ed; border: 1px solid #fdba74; border-radius: 6px;">
            <div style="font-size: 13px; color: #9a3412;">
              <strong>Deallocate Assets:</strong><br/>
              All assets (computers, mobile devices, equipment, etc.) assigned to this user will be deallocated and returned to available inventory.
            </div>
          </div>
          
          <div style="margin-top: 15px; padding: 10px; background: #fef3c7; border: 1px solid #fbbf24; border-radius: 6px;">
            <div style="font-size: 13px; color: #92400e; font-weight: 500;">⚠️ This action will trigger real changes in the asset management system</div>
          </div>
        </div>
      `;
    } else if (integrationType === 'user_deactivated') {
      // User deactivation modal
      const terminationReason = customFields.termination_reason || customFields.reason || 'Not specified';
      const terminationComment = customFields.comment || customFields.termination_comment || '';
      
      modalContent = `
        <div style="text-align: left;">
          <p><strong>User Account to Deactivate:</strong></p>
          <div style="margin: 10px 0; padding: 15px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px;">
            <div style="font-size: 16px; font-weight: bold; color: #991b1b; margin-bottom: 8px;">${userName}</div>
          </div>
          
          ${terminationReason !== 'Not specified' ? `
          <p style="margin-top: 15px;"><strong>Termination Reason:</strong></p>
          <div style="margin: 10px 0; padding: 10px; background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 6px;">
            <div style="font-size: 13px; color: #374151;">${terminationReason}</div>
          </div>
          ` : ''}
          
          ${terminationComment ? `
          <p style="margin-top: 15px;"><strong>Additional Notes:</strong></p>
          <div style="margin: 10px 0; padding: 10px; background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 6px;">
            <div style="font-size: 13px; color: #374151;">${terminationComment}</div>
          </div>
          ` : ''}
          
          <p style="margin-top: 15px;"><strong>Action:</strong></p>
          <div style="margin: 10px 0; padding: 10px; background: #fff7ed; border: 1px solid #fdba74; border-radius: 6px;">
            <div style="font-size: 13px; color: #9a3412;">
              <strong>Deactivate User Account:</strong><br/>
              This user account will be deactivated (user_status = 0) and the user will no longer be able to access any systems or applications.
            </div>
          </div>
          
          <div style="margin-top: 15px; padding: 10px; background: #fef3c7; border: 1px solid #fbbf24; border-radius: 6px;">
            <div style="font-size: 13px; color: #92400e; font-weight: 500;">⚠️ This action will trigger real changes in the user management system</div>
          </div>
        </div>
      `;
    } else {
      // Default modal for user_created, asset_created, etc.
      modalContent = `
        <div style="text-align: left;">
          <p><strong>User Details:</strong></p>
          <div style="margin: 10px 0; padding: 15px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px;">
            <div style="font-size: 16px; font-weight: bold; color: #1f2937; margin-bottom: 8px;">${userName}</div>
            <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">Department: ${department}</div>
            <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">Role/Position: ${role}</div>
            ${additionalDetails.length > 0 ? additionalDetails.map(detail => `<div style="font-size: 12px; color: #9ca3af;">${detail}</div>`).join('') : ''}
          </div>
          ${softwareList.length > 0 ? `
            <p style="margin-top: 15px;"><strong>Required Software Access:</strong></p>
            <div style="margin: 10px 0; padding: 10px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 6px;">
              ${softwareList.map(software => `<div style="font-size: 12px; color: #1e40af;">• ${software}</div>`).join('')}
            </div>
          ` : ''}
          <div style="margin-top: 15px; padding: 10px; background: #fef3c7; border: 1px solid #fbbf24; border-radius: 6px;">
            <div style="font-size: 13px; color: #92400e; font-weight: 500;">⚠️ This action will trigger real changes in the target system</div>
          </div>
        </div>
      `;
    }

    // Show confirmation dialog
    const result = await Swal.fire({
      title: `Execute ${integrationType.replace('_', ' ').toUpperCase()} Integration?`,
      html: modalContent,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3b82f6',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Execute Integration',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      focusCancel: true,
      width: '500px'
    });

    if (!result.isConfirmed) return;

    setLoading(prev => ({ ...prev, [integrationType]: true }));

    try {
      // Generate user email using Name.Surname@silulumanzi.com pattern
      const generateUserEmail = (fullName) => {
        if (!fullName || typeof fullName !== 'string') {
          return 'user@silulumanzi.com'; // Fallback email
        }
        
        // Clean and split the name
        const nameParts = fullName.trim()
          .split(/\s+/) // Split by whitespace
          .filter(part => part.length > 0) // Remove empty parts
          .map(part => part.replace(/[^a-zA-Z]/g, '')) // Remove non-letters
          .filter(part => part.length > 0); // Remove empty parts after cleaning
        
        if (nameParts.length === 0) {
          return 'user@silulumanzi.com'; // Fallback if no valid name parts
        }
        
        let email;
        if (nameParts.length === 1) {
          // Only one name part
          email = `${nameParts[0].toLowerCase()}@silulumanzi.com`;
        } else {
          // Use first name and last name (last element)
          const firstName = nameParts[0];
          const lastName = nameParts[nameParts.length - 1];
          email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}@silulumanzi.com`;
        }
        
        return email;
      };

      const userEmail = generateUserEmail(userName);

      // Helper to extract a number from either a number or a string like "Department ID: 110"
      const asNumber = (val) => {
        if (val === null || val === undefined) return undefined;
        if (typeof val === 'number') return val;
        const m = String(val).match(/(\d+)/);
        return m ? Number(m[1]) : undefined;
      };

      const integrationData = {
        ticket_id: ticketId,
        integration_type: integrationType,
        workflow_data: {
          ...customFields,
          // Add extracted user information to ensure backend has proper data
          user_full_name: userName,
          user_email: userEmail, // Add generated email
          department: department,
          role: role,
          position: role,
          // Provide explicit numeric fields to prevent backend mixups
          department_id: asNumber(customFields.department),
          division: asNumber(customFields.division ?? ticket?.requester_division ?? ticket?.division ?? ticket?.department),
          role_id: asNumber(customFields.role ?? customFields.position ?? customFields.post),
          staff_id: customFields.staff_id || customFields.employee_id || ticket?.requester_id,
          // Explicitly carry the new values for staff movement
          new_department: asNumber(customFields.new_department ?? customFields.department_id ?? customFields.department),
          new_position: asNumber(customFields.new_position ?? customFields.role_id ?? customFields.role ?? customFields.post)
        },
        execution_notes: executionNotes[integrationType] || '',
        ticket_type: ticket.ticket_type || (integrationType === 'user_created' ? 'new_user' : 'general')
      };

      const response = await fetch(`${API_BASE_URL}/api/helpdesk/workflows/execute-integration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('user_token')}`
        },
        body: JSON.stringify(integrationData)
      });

  const data = await response.json();

      if (data.status === 1) {
        // Cache the integration result in component state
        setIntegrationCache(prev => ({
          ...prev,
          [integrationType]: data.data
        }));
        
        // Update ticket with integration status
        const updateData = {};
        updateData[integrationType] = 'yes';
        
  const updateResponse = await updateTicket(ticketId, updateData);
        
        if (updateResponse.status === 1) {
          // Success message will be shown by parent component via onIntegrationExecuted callback
          onIntegrationExecuted?.(integrationType, data.data);
        } else {
          toast.error('Integration executed but status update failed');
        }
      } else {
        toast.error(data.message || `Failed to execute ${integrationType} integration`);
      }
    } catch (error) {
      console.error(`Error executing ${integrationType} integration:`, error);
      toast.error(`Error executing integration: ${error.message}`);
    } finally {
      setLoading(prev => ({ ...prev, [integrationType]: false }));
    }
  };

  // Asset picker helpers for asset allocation step
  const fetchAvailableAssets = async () => {
    try {
      setAssetsLoading(true);
      setAssetsFallbackNote('');
  // Reuse existing assets model with pagination params to get a reasonable chunk
  const params = { page: 1, pageSize: 100, unallocated: 1 };
  const res = await (await import('../../../../models/assetsModel')).getAssets(params);
      // Normalize payload shape and extract rows robustly
      // res comes from assetsModel.getAssets -> response.data (full API payload)
      let rows = [];
      if (Array.isArray(res?.data?.data?.rows)) {
        rows = res.data.data.rows; // Typical shape: { status, data: { data: { rows: [...] } } }
      } else if (Array.isArray(res?.data?.rows)) {
        rows = res.data.rows; // Fallback: { data: { rows: [...] } }
      } else if (Array.isArray(res?.rows)) {
        rows = res.rows; // Direct rows
      } else if (Array.isArray(res)) {
        rows = res; // Already an array
      } else {
        rows = [];
      }
      // Ensure it's an array
      const list = Array.isArray(rows) ? rows : [];
      // If row has user_full_name or allocated user, treat as allocated; keep unallocated
  const unallocated = list.filter(a => !a?.user_full_name && !a?.allocate_staff_id);
      if (unallocated.length > 0) {
        setAvailableAssets(unallocated);
      } else {
        // If we couldn't detect unallocated assets, fall back to showing all and inform the user
        setAvailableAssets(list);
        if (list.length > 0) {
          setAssetsFallbackNote('No clearly unallocated assets detected. Showing all assets instead.');
        }
      }
    } catch (e) {
      console.error('Failed to load available assets:', e);
      toast.error('Failed to load assets');
      setAvailableAssets([]);
    } finally {
      setAssetsLoading(false);
    }
  };

  const openAssetPicker = async () => {
    await fetchAvailableAssets();
    setAssetPickerOpen(true);
  };

  const closeAssetPicker = () => {
    setAssetPickerOpen(false);
    setSelectedAssetIds([]);
  };

  const toggleAssetSelection = (assetId) => {
    setSelectedAssetIds(prev => prev.includes(assetId) ? prev.filter(id => id !== assetId) : [...prev, assetId]);
  };

  const allocateSelectedAssets = async () => {
    if (selectedAssetIds.length === 0) {
      toast.warn('Select at least one asset to allocate');
      return;
    }

    // If any selected asset is already allocated and reassigning is disabled, warn and stop
    const selectedAssets = availableAssets.filter(a => selectedAssetIds.includes(a.asset_id));
    const alreadyAllocated = selectedAssets.filter(a => a?.user_full_name || a?.allocate_staff_id);
    if (alreadyAllocated.length > 0 && !allowReassign) {
      toast.warn('Some selected assets are already allocated. Enable "Allow reassign" to proceed.');
      return;
    }

    // If reassigning, ask for confirmation
    if (alreadyAllocated.length > 0) {
      const names = alreadyAllocated.map(a => `${a.asset_name}${a.user_full_name ? ` (to ${a.user_full_name})` : ''}`).join(', ');
      const confirm = await Swal.fire({
        title: 'Reassign allocated assets?',
        html: `<div style="text-align:left">You're about to allocate assets that are currently assigned: <br/><strong>${names}</strong><br/><br/>This will create a new allocation record.</div>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Proceed',
        cancelButtonText: 'Cancel'
      });
      if (!confirm.isConfirmed) return;
    }

    // Determine the target user id from integration results if present
    let targetUserId = null;
    try {
      const cf = ticket?.custom_fields ? (typeof ticket.custom_fields === 'string' ? JSON.parse(ticket.custom_fields) : ticket.custom_fields) : {};
      console.log('🔍 Asset Allocation Debug:', {
        custom_fields: cf,
        integration_results: cf?.integration_results,
        user_created: cf?.integration_results?.user_created,
        user_created_flag: ticket?.user_created,
        integrationCache: integrationCache
      });
      
      // Try to get from custom_fields first
      const integ = cf?.integration_results?.user_created;
      targetUserId = integ?.user_id || null;
      
      // Fallback to cached integration result if database doesn't have it yet
      if (!targetUserId && integrationCache?.user_created?.user_id) {
        targetUserId = integrationCache.user_created.user_id;
        console.log('✅ Using cached user_id:', targetUserId);
      }
      
      // Last resort: If user was created but we don't have the ID, try to find it by email
      if (!targetUserId && ticket?.user_created === 'yes') {
        console.log('⚠️ User created but no user_id found. Attempting to look up by email...');
        
        // Try to construct email from custom fields
        let userEmail = cf?.user_email || cf?.email;
        
        // If no direct email, construct from first_name and last_name
        if (!userEmail && cf?.first_name && cf?.last_name) {
          const firstName = cf.first_name.trim().toLowerCase().replace(/[^a-z]/g, '');
          const lastName = cf.last_name.trim().toLowerCase().replace(/[^a-z]/g, '');
          userEmail = `${firstName}.${lastName}@silulumanzi.com`;
          console.log('📧 Constructed email from name fields:', userEmail);
        }
        
        // Also try full name pattern
        if (!userEmail && cf?.user_full_name) {
          const nameParts = cf.user_full_name.trim().split(/\s+/).filter(p => p.length > 0);
          if (nameParts.length >= 2) {
            const firstName = nameParts[0].toLowerCase().replace(/[^a-z]/g, '');
            const lastName = nameParts[nameParts.length - 1].toLowerCase().replace(/[^a-z]/g, '');
            userEmail = `${firstName}.${lastName}@silulumanzi.com`;
            console.log('📧 Constructed email from user_full_name:', userEmail);
          }
        }
        
        if (userEmail) {
          try {
            const { API_BASE_URL } = await import('../../../../config');
            const lookupUrl = `${API_BASE_URL}/api/users/lookup-by-email?email=${encodeURIComponent(userEmail)}`;
            console.log('📡 Lookup URL:', lookupUrl);
            const response = await fetch(lookupUrl, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('user_token')}`
              }
            });
            const data = await response.json();
            console.log('📡 User lookup response:', data);
            if (data.status === 1 && data.data?.user_id) {
              targetUserId = data.data.user_id;
              console.log('✅ Found user by email lookup:', targetUserId);
            } else {
              console.error('❌ User lookup failed:', data.message || 'User not found');
            }
          } catch (err) {
            console.error('Failed to lookup user by email:', err);
          }
        } else {
          console.error('❌ Cannot construct email - missing name fields. Available fields:', Object.keys(cf));
        }
      }
    } catch (e) {
      console.error('Error parsing custom_fields for asset allocation:', e);
    }

    if (!targetUserId) {
      toast.error('Cannot determine user ID for asset allocation. User may need to be created first, or there was an error storing the user ID.');
      return;
    }

    try {
      setAllocating(true);
      const ticketId = ticket?.id || ticket?.ticket_id || ticket?.ID || ticket?.Ticket_ID;
      const { allocateAsset } = await import('../../../../models/assetsModel');
      for (const assetId of selectedAssetIds) {
        await allocateAsset({
          asset_id: assetId,
          allocate_staff_id: targetUserId,
          asset_allocation_date: new Date().toISOString().split('T')[0],
          asset_allocation_notes: `Auto-allocated from ticket #${ticketId}`,
          declaration: 1,
          ticket_id: ticketId
        });
      }

      // Mark asset_created and refresh
      if (ticketId) {
        const resp = await (await import('../../../../models/helpdeskModel')).updateTicket(ticketId, { asset_created: 'yes' });
        if (resp?.status === 1) {
          toast.success('Assets allocated and ticket updated');
          onIntegrationExecuted?.('asset_created', { allocated_asset_ids: selectedAssetIds });
          // Fire-and-forget: send declaration email to the allocated user
          try {
            const { sendDeclarationEmail } = await import('../../../../models/assetsModel');
            await sendDeclarationEmail({ ticket_id: ticketId, user_id: targetUserId, asset_ids: selectedAssetIds });
            toast.success('Declaration email sent to user');
          } catch (err) {
            console.warn('Failed to send declaration email:', err);
          }
        } else {
          toast.warn('Assets allocated but ticket update failed');
        }
      } else {
        toast.warn('Assets allocated; unable to update ticket - missing ticket id');
      }
      closeAssetPicker();
    } catch (e) {
      console.error('Allocation failed:', e);
      toast.error('Failed to allocate selected assets');
    } finally {
      setAllocating(false);
    }
  };

  const getIntegrationActions = () => {
    const ticketType = ticket?.ticket_type;
    const actions = [];

    // Parse custom fields to check requirements
    let customFields = {};
    try {
      customFields = typeof ticket?.custom_fields === 'string' 
        ? JSON.parse(ticket.custom_fields) 
        : ticket?.custom_fields || {};
    } catch (e) {
      customFields = {};
    }

    // Access Termination Workflow
    if (ticketType === 'access_termination') {
      // Asset Deallocation
      if (getIntegrationStatus(ticket, 'asset_deallocated') !== 'yes') {
        actions.push({
          key: 'asset_deallocated',
          label: 'Deallocate Assets',
          icon: Monitor,
          description: 'Remove asset allocations from terminated user',
          color: 'orange'
        });
      }

      // User Account Deactivation (only after assets are deallocated)
      if (getIntegrationStatus(ticket, 'user_deactivated') !== 'yes' && getIntegrationStatus(ticket, 'asset_deallocated') === 'yes') {
        actions.push({
          key: 'user_deactivated',
          label: 'Deactivate User Account',
          icon: UserMinus,
          description: 'Disable user account and revoke access',
          color: 'red'
        });
      }
    }

    // User Account Integration
    if (['new_user', 'staff_movement'].includes(ticketType) && getIntegrationStatus(ticket, 'user_created') !== 'yes') {
      const label = ticketType === 'staff_movement' ? 'Update Role & Department' : 'Create User Account';
      const description = ticketType === 'staff_movement' 
        ? 'Update user role and department assignment'
        : 'Create portal account with role assignments';
      
      actions.push({
        key: 'user_created',
        label: label,
        icon: UserPlus,
        description: description,
        color: 'blue'
      });
    }

    // Asset Allocation Integration (only after user account is created)
    // Only show if Computer was selected in custom fields (applies to all workflow types)
    const shouldShowAssetAllocation = customFields.software_computer === true || customFields.software_computer === 'true';

    if (
      ['new_user', 'staff_movement', 'change_request'].includes(ticketType) &&
      getIntegrationStatus(ticket, 'asset_created') !== 'yes' &&
      getIntegrationStatus(ticket, 'user_created') === 'yes' &&
      shouldShowAssetAllocation
    ) {
      actions.push({
        key: 'asset_created',
        label: 'Allocate Assets',
        icon: Monitor,
        description: 'Allocate and track hardware assets',
        color: 'green'
      });
    }

    return actions;
  };

  const availableActions = getIntegrationActions();

  if (!isReadyForImplementation()) {
    return (
      <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200">
        <div className="flex items-center">
          <AlertTriangle className="w-6 h-6 text-yellow-600 mr-3" />
          <div>
            <h3 className="text-lg font-medium text-yellow-900">
              Waiting for Approvals
            </h3>
            <p className="text-yellow-700 mt-1">
              All required approvals must be completed before implementation can begin.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (availableActions.length === 0) {
    return (
      <div className="bg-green-50 rounded-lg p-6 border border-green-200">
        <div className="flex items-center">
          <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
          <div>
            <h3 className="text-lg font-medium text-green-900">
              All Integrations Complete
            </h3>
            <p className="text-green-700 mt-1">
              All required cross-module integrations have been successfully executed.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <Cpu className="w-5 h-5 mr-2 text-blue-600" />
        Execute Cross-Module Integrations
      </h3>

      {/* Implementation Guidelines */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-900">Implementation Guidelines</h4>
            <ul className="mt-2 text-sm text-blue-800 space-y-1">
              <li>• Execute integrations in the order they appear</li>
              <li>• Verify custom field data before execution</li>
              <li>• Add execution notes for audit trail</li>
              <li>• Monitor integration status for completion</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {availableActions.map((action) => {
          const ActionIcon = action.icon;
          const isLoading = loading[action.key];

          return (
            <div key={action.key} className={`border border-${action.color}-200 rounded-lg p-4`}>
              <div className="flex items-start space-x-4">
                <div className={`flex-shrink-0 p-2 bg-${action.color}-100 rounded-lg`}>
                  <ActionIcon className={`w-6 h-6 text-${action.color}-600`} />
                </div>

                <div className="flex-1 min-w-0">
                  <h4 className="text-lg font-medium text-gray-900 mb-1">
                    {action.label}
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    {action.description}
                  </p>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Execution Notes (Optional)
                    </label>
                    <textarea
                      value={executionNotes[action.key] || ''}
                      onChange={(e) => setExecutionNotes(prev => ({
                        ...prev,
                        [action.key]: e.target.value
                      }))}
                      placeholder="Add any notes about this integration execution..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <Button
                    onClick={() => {
                      if (action.key === 'asset_created') {
                        openAssetPicker();
                      } else {
                        executeIntegration(action.key);
                      }
                    }}
                    variant="primary"
                    icon={action.icon}
                    disabled={isLoading}
                    className="w-full sm:w-auto"
                  >
                    {isLoading ? `Executing ${action.label}...` : `Execute ${action.label}`}
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Inline Asset Picker Modal */}
      {assetPickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold">Allocate Assets</h4>
              <button onClick={closeAssetPicker} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <p className="text-sm text-gray-600 mb-2">Select available devices to allocate to the user created for this ticket.</p>
            <div className="mb-3">
              <input
                value={assetSearch}
                onChange={(e) => setAssetSearch(e.target.value)}
                placeholder="Search by name, vendor, serial..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {assetsLoading ? (
              <div className="py-10 text-center">Loading assets…</div>
            ) : (
              <div className="max-h-80 overflow-auto border rounded-md">
                {assetsFallbackNote && (
                  <div className="px-3 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-200">{assetsFallbackNote}</div>
                )}
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-2 w-10"></th>
                      <th className="p-2 text-left">Name</th>
                      <th className="p-2 text-left">Category</th>
                      <th className="p-2 text-left">Vendor</th>
                      <th className="p-2 text-left">Serial</th>
                      <th className="p-2 text-left">Allocated To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availableAssets
                      .filter(a => {
                        if (!assetSearch) return true;
                        const q = assetSearch.toLowerCase();
                        return (
                          String(a.asset_name || '').toLowerCase().includes(q) ||
                          String(a.vendor || '').toLowerCase().includes(q) ||
                          String(a.category || '').toLowerCase().includes(q) ||
                          String(a.asset_serial || '').toLowerCase().includes(q)
                        );
                      })
                      .map(a => {
                      const isAllocated = Boolean(a?.user_full_name || a?.allocate_staff_id);
                      return (
                        <tr key={a.asset_id} className="border-t">
                          <td className="p-2 text-center">
                            <input
                              type="checkbox"
                              checked={selectedAssetIds.includes(a.asset_id)}
                              onChange={() => toggleAssetSelection(a.asset_id)}
                              disabled={isAllocated}
                              title={isAllocated ? 'Already allocated' : 'Select this asset'}
                            />
                          </td>
                          <td className="p-2">{a.asset_name}</td>
                          <td className="p-2">{a.category}</td>
                          <td className="p-2">{a.vendor}</td>
                          <td className="p-2">{a.asset_serial}</td>
                          <td className="p-2">{a.user_full_name || '—'}</td>
                        </tr>
                      );
                    })}
                    {availableAssets.length === 0 && (
                      <tr>
                        <td colSpan={6} className="p-6 text-center text-gray-500">No unallocated assets available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-center justify-between mt-4">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={allowReassign} onChange={(e) => setAllowReassign(e.target.checked)} />
                Allow reassign (select assets even if already allocated)
              </label>
              <div className="flex gap-3">
              <Button variant="secondary" onClick={closeAssetPicker} disabled={allocating}>Cancel</Button>
              <Button onClick={allocateSelectedAssets} disabled={allocating || selectedAssetIds.length === 0}>{allocating ? 'Allocating…' : 'Allocate Selected'}</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
